<template>
  <v-card class="observatory-list" flat>
    <v-card align="left" color="#dddddd" tile>
      <div class="body-2 font-weight-bold px-2 py-1">
        RisKma | 河川・降雨状況一覧
      </div>
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="orange"
      ></v-progress-linear>
    </v-card>

    <v-card
      class="d-flex mx-2 my-1 overflow-y-auto"
      height="calc(100dvh - 40px)"
      tile
      flat
    >
      <!-- 雨量 -->
      <v-card tile flat class="pa-2">
        <v-card v-for="ob in rains" :key="ob.id" class="mb-2 px-2" height="190">
          <div class="chart-title">{{ ob.name }}</div>
          <div class="d-flex">
            <v-card width="100" class="d-flex justify-center align-center" flat>
              <v-simple-table
                dense
                v-if="ob.timeSeries && ob.timeSeries.length > 0"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">
                        時間
                      </th>
                      <th class="text-left">
                        10分
                      </th>
                      <th class="text-left">
                        累加
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="ts in ob.timeSeries.slice(-6)" :key="ts.date">
                      <td>{{ toTableFormat(ts.date) }}</td>
                      <td class="text-right">{{ ts.min10 }}</td>
                      <td class="text-right">{{ ts.total }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <div v-else>
                欠測
              </div>
            </v-card>
            <v-card outlined class="ml-3" width="200" height="150">
              <RainChart
                v-if="ob.timeSeries && ob.timeSeries.length > 0"
                :height="150"
                :selected-observatory="ob"
                :displayHours="0.5"
                :displayInterval="10"
                :currentDate="ob.currentDate"
              />
            </v-card>
          </div>
        </v-card>
      </v-card>

      <!-- 水位・カメラ -->
      <v-card class="py-2 d-flex align-content-start flex-wrap" tile flat>
        <v-card
          v-for="ob in stages"
          :key="ob.id"
          class="mb-2 px-2 mx-1"
          width="430"
          height="190"
        >
          <div class="chart-title">{{ ob.name }}</div>
          <div class="d-flex align-center" style="height: 155px">
            <v-card width="90" class="d-flex justify-center align-center" flat>
              <v-simple-table
                v-if="ob.timeSeries && ob.timeSeries.length > 0"
                dense
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">
                        時間
                      </th>
                      <th class="text-left">
                        水位
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="ts in ob.timeSeries.slice(-6)" :key="ts.date">
                      <td>{{ toTableFormat(ts.date) }}</td>
                      <td>{{ ts.level }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <div v-else>
                <template v-if="ob.kiki === true">
                  観測開始<br />水位以下
                </template>
                <template v-else>
                  欠測
                </template>
              </div>
            </v-card>
            <v-card width="110" height="140" color="#cccccc">
              <v-img
                v-if="ob.cameraUrl"
                :src="ob.cameraUrl"
                height="140"
                width="110"
                @click="onClickImage(ob)"
              ></v-img>
            </v-card>
            <v-card outlined class="ml-2" width="200" height="150">
              <StageChart
                v-if="ob.timeSeries && ob.timeSeries.length > 0"
                :height="150"
                :selected-observatory="ob"
                :displayHours="0"
                :chartTimeSeries="ob.chartTimeSeries"
                :mesuredLatestValue="ob.currentDate"
                levelUnit="m"
              />
            </v-card>
          </div>
        </v-card>
      </v-card>
    </v-card>

    <v-dialog v-if="bigSizeImageOb" v-model="dialog" max-width="1000">
      <v-row
        no-gutters
        align="center"
        justify="end"
        style="background-color: white"
      >
        <h3 class="pa-2">{{ bigSizeImageOb.name }}</h3>
        <v-spacer></v-spacer>
        <v-btn icon @click="dialog = false" class="mr-2">
          <v-icon>
            mdi-close
          </v-icon>
        </v-btn>
      </v-row>
      <v-img :src="bigSizeImageOb.cameraUrl" max-width="1000px"></v-img>
    </v-dialog>
  </v-card>
</template>
<script>
import * as axiosHelper from "@/utils/axiosHelper";
import moment from "moment";
import { OBSERVATORY } from "@/enums/Type";
import RainChart from "@/components/base-map/details/RainChart.vue";
import StageChart from "@/components/base-map/details/StageChart.vue";

const DATE_FORMAT = "YYYY/MM/DD HH:mm";
const observatoryGroups = [
  {
    type: OBSERVATORY.RAIN.code,
    items: [
      { id: "4353_101" },
      { id: "4353_104" },
      { id: "4353_107" },
      { id: "30000_56192" }
    ]
  },
  {
    type: OBSERVATORY.STAGE.code,
    items: [
      { id: "4353_101", cameraId: "99_17000_2_10" },
      { id: "4353_104", cameraId: "104353119" },
      { id: "4353_110", cameraId: "104353120" },
      { id: "4353_88", cameraId: "99_17000_2_37" },
      { id: "4353_105", cameraId: "104353118" },
      { id: "17000_72", cameraId: "104353068" },
      { id: "17000_14", cameraId: "104353008" },
      { id: "17000_27", cameraId: "104353009" },
      { id: "17000_116", cameraId: "104353117" },
      { id: "17000_28", cameraId: "104353010" },
      { id: "17000_115", cameraId: "104353116" },
      { id: "17000_13", cameraId: "104353007" },
      { id: "17000_71", cameraId: "104353067" },
      { id: "17000_139", cameraId: "104353171" }
    ]
  }
];

export default {
  name: "ObservatoryList",
  components: { StageChart, RainChart },
  data() {
    return {
      baseDate: moment().utc(),
      rains: [],
      stages: [],
      dialog: false,
      bigSizeImageOb: null,
      isLoading: false,
      observatories: []
    };
  },

  computed: {
    //
  },

  async created() {
    this.isLoading = true;
    await this.fetchObservatories();

    for (const group of observatoryGroups) {
      const targets = [];
      const processes = group.items.map(item => {
        const cameraId = item.cameraId;
        return (async () => {
          const target = this.observatories.find(
            ob => ob.id === item.id && ob.type === group.type
          );
          if (target === undefined) return;
          const data = await this.fetchData(target);
          if (cameraId) {
            const cameraObservatory = this.observatories.find(
              ob => ob.id === cameraId && ob.type === OBSERVATORY.CAMERA.code
            );
            if (cameraObservatory !== undefined) {
              const cameraData = await this.fetchData(cameraObservatory);
              if (cameraData.timeSeries.length > 0) {
                data.cameraUrl =
                  cameraData.timeSeries[cameraData.timeSeries.length - 1].url;
              }
            }
          }
          targets.push(data);
        })();
      });
      await Promise.all(processes);

      // 並び替え
      const sortedTargets = group.items
        .map(item => {
          return targets.find(target => target.id === item.id);
        })
        .filter(target => target !== undefined);

      if (group.type === OBSERVATORY.RAIN.code) {
        this.rains = sortedTargets;
      } else if (group.type === OBSERVATORY.STAGE.code) {
        this.stages = sortedTargets;
      }
    }
    this.isLoading = false;
  },

  methods: {
    async fetchObservatories() {
      const res = await axiosHelper.get("/observatories", {
        domain: "hodatsushimizu.riskma.jp"
      });
      if (res === null || !res.observatories) {
        this.observatories = [];
      } else {
        this.observatories = res.observatories;
      }
    },

    async fetchData(ob) {
      const params = {
        date: this.baseDate.format(DATE_FORMAT),
        id: ob.id,
        type: ob.type,
        rain_forecast: false
      };
      if (ob.type === OBSERVATORY.RAIN.code) {
        params.hours = 3;
      }
      if (ob.type === OBSERVATORY.CAMERA.code) {
        if (ob.dynamodb) {
          params["dynamodb"] = ob.dynamodb;
        }
        if (ob.bucket) {
          params["bucket"] = ob.bucket;
        }
        if (ob.prefix) {
          params["prefix"] = ob.prefix;
        }
      }
      const response = await axiosHelper.get("/observatories/data", params);
      if (response === null) {
        throw new Error("fetch failed : observatories/data");
      }

      const data = Object.assign({}, ob);
      data.timeSeries = response.timeSeries;
      data.chartTimeSeries = response.timeSeries.map(ts => ts.date);
      data.currentDate = this.baseDate
        .subtract(this.baseDate.minute() % 10, "minutes")
        .format(DATE_FORMAT);

      return data;
    },

    toTableFormat(date) {
      return moment
        .utc(date, DATE_FORMAT)
        .local()
        .format("HH:mm");
    },
    onClickImage(ob) {
      if (ob.cameraUrl === undefined) return;
      this.bigSizeImageOb = ob;
      this.dialog = true;
    }
  }
};
</script>
<style scoped lang="scss">
.chart-title {
  color: #666666;
  font-size: 14px;
  font-weight: bold;
  text-decoration: underline;
  padding: 4px 0;
}
.observatory-list {
  .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
    padding: 0 2px;
    height: 20px;
    color: #515151;
    font-size: 14px;
  }
  .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
    padding: 0 2px;
    height: 22px;
  }
  .v-image {
    cursor: pointer;
  }
}
</style>
